/**
 * Shared design tokens and style objects for the Warehouse Storage Manager feature.
 *
 * Consumed by: WarehouseStorageManager, StorageShelf, StoragePositionCell,
 * ResourcePanel, CellDetailPopover, StorageStatusBar.
 *
 * ACCENT colour comes from theme.palette.primary.main (#7FE1A7) — do NOT
 * hardcode it here; use the `accent` local variable in each component instead.
 * Only static colours that don't depend on the theme live here.
 */

export const TYPE_META = {
  Part: { label: 'Parts', color: '#2196f3' },
  Fixture: { label: 'Fixture', color: '#ff9800' },
  RawMaterial: { label: 'Raw Materials', color: '#00bcd4' },
  Tool: { label: 'Tools', color: '#9c27b0' }
}

export const TYPE_KEYS = ['Part', 'Fixture', 'RawMaterial', 'Tool']

export const ACCENT = '#7FE1A7'
export const PENDING_COLOR = '#ff9800'
const BORDER_SUBTLE = '1px solid rgba(255,255,255,0.07)'

export const dot = (size, color) => ({ width: size, height: size, borderRadius: '50%', bgcolor: color })

export const styles = {
  panelCard: {
    background: 'linear-gradient(180deg, rgba(40,40,39,0.95) 0%, rgba(33,33,33,0.95) 100%)',
    border: BORDER_SUBTLE,
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
  },
  shelfCard: {
    background: 'linear-gradient(135deg, rgba(40,40,39,0.95), rgba(33,33,33,0.95))',
    border: BORDER_SUBTLE,
    borderRadius: 2,
    backdropFilter: 'blur(8px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.35)'
  },
  textField: {
    flexShrink: 0,
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255,255,255,0.03)',
      minHeight: 48,
      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(127,225,167,0.35)' },
      '&.Mui-focused fieldset': { borderColor: ACCENT }
    },
    '& input': { color: '#ccc' },
    '& ::placeholder': { color: '#444', opacity: 1 }
  },
  select: {
    color: '#ccc',
    minHeight: 48,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(127,225,167,0.35)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
    '& .MuiSvgIcon-root': { color: ACCENT }
  },
  accentBtn: {
    color: ACCENT,
    borderColor: 'rgba(127,225,167,0.35)',
    minHeight: 44,
    px: 2,
    '&:hover': { borderColor: ACCENT, bgcolor: 'rgba(127,225,167,0.05)' },
    '&:disabled': { color: '#444', borderColor: '#303030' }
  },
  axisLabel: { fontSize: '0.75rem', color: '#888' },
  sectionTitle: {
    fontWeight: 700,
    color: ACCENT,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    flexShrink: 0
  },
  pendingDot: {
    position: 'absolute',
    top: 3,
    left: 3,
    width: 7,
    height: 7,
    borderRadius: '50%',
    bgcolor: PENDING_COLOR
  },
  stackBadge: {
    position: 'absolute',
    top: 3,
    right: 3,
    bgcolor: PENDING_COLOR,
    color: '#000',
    borderRadius: '4px',
    px: 0.4,
    fontSize: '0.55rem',
    fontWeight: 700,
    lineHeight: 1.5
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    pb: 1.5,
    borderBottom: BORDER_SUBTLE,
    flexShrink: 0
  },
  storageSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    p: 1.5,
    borderRadius: 2,
    bgcolor: 'rgba(40,40,39,0.7)',
    border: BORDER_SUBTLE,
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    flexWrap: 'wrap',
    px: 2,
    py: 1,
    borderTop: '1px solid rgba(255,255,255,0.05)',
    bgcolor: 'rgba(18,18,18,0.6)',
    borderRadius: 1,
    flexShrink: 0
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '55%',
    gap: 2
  },
  selectedResourceBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    bgcolor: 'rgba(127,225,167,0.08)',
    border: '1px solid rgba(127,225,167,0.35)',
    borderRadius: 1.5,
    px: 1.5,
    py: 0.8,
    flexShrink: 0
  },
  popoverPaper: {
    bgcolor: '#212121',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 2,
    p: 2,
    minWidth: 300,
    boxShadow: '0 12px 40px rgba(0,0,0,0.6)'
  },
  truncated: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
}

export const shelfHeaderSx = (isExpanded, accent = ACCENT) => ({
  px: 2,
  py: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  bgcolor: isExpanded ? `${accent}0d` : 'transparent',
  borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.06)' : 'none',
  '&:hover': { bgcolor: `${accent}12` },
  transition: 'background 0.2s',
  minHeight: 60,
  userSelect: 'none'
})
